import { api } from '../api.ts';
import { html, mount } from '../html.ts';
import { currentIntlLocale, t, tk, today } from '../i18n.ts';
import { setQuery } from '../router.ts';
import { isAdmin } from '../state.ts';
import { openFormDialog, toastError } from '../ui.ts';
import { pageHeader } from './common.ts';

const KIND_LABEL: Record<string, string> = { task: tk('Aufgabe'), gate: tk('Gate'), risk_review: tk('Risikoprüfung'), phase_end: tk('Phasenende') };
const KIND_TAB: Record<string, string> = { task: 'tasks', gate: 'gates', risk_review: 'risks', phase_end: 'timeline' };
const WEEKDAYS = [tk('Mo'), tk('Di'), tk('Mi'), tk('Do'), tk('Fr'), tk('Sa'), tk('So')];

const iso = (date: Date) => date.toISOString().slice(0, 10);

export async function calendarView(container: HTMLElement, _params: Record<string, string>, query: URLSearchParams): Promise<void> {
  let month = /^\d{4}-\d{2}$/.test(query.get('month') ?? '') ? query.get('month')! : today().slice(0, 7);

  async function draw() {
    const first = new Date(`${month}-01T00:00:00Z`);
    const gridStart = new Date(first);
    gridStart.setUTCDate(1 - ((first.getUTCDay() + 6) % 7));
    const days = Array.from({ length: 42 }, (_, i) => {
      const d = new Date(gridStart);
      d.setUTCDate(gridStart.getUTCDate() + i);
      return iso(d);
    });
    const { items } = await api.get<{ items: any[] }>(`/calendar?from=${days[0]}&to=${days[41]}`);
    const byDay = new Map<string, any[]>();
    for (const item of items) byDay.set(item.date, [...(byDay.get(item.date) ?? []), item]);
    const title = new Intl.DateTimeFormat(currentIntlLocale(), { month: 'long', year: 'numeric', timeZone: 'UTC' }).format(first);
    const now = today();
    mount(
      container,
      html`${pageHeader(
        t('Termine'),
        t('Kalender'),
        t('Fällige Aufgaben, Gates, Risikoprüfungen und Phasenenden über alle Projekte.'),
        html`<button class="p-button p-button--ghost" type="button" data-shift="-1" aria-label="${t('Vorheriger Monat')}">‹</button>
          <button class="p-button p-button--ghost" type="button" data-shift="0">${t('Heute')}</button>
          <button class="p-button p-button--ghost" type="button" data-shift="1" aria-label="${t('Nächster Monat')}">›</button>
          ${isAdmin() ? html`<button class="p-button" type="button" data-subscribe>${t('Abonnieren (ICS)')}</button>` : ''}`,
      )}
      <section class="pma-calendar" aria-labelledby="cal-title">
        <h2 id="cal-title" class="pma-calendar__title">${title}</h2>
        <div class="pma-calendar__grid" role="grid" aria-labelledby="cal-title">
          <div class="pma-calendar__head" role="row">${WEEKDAYS.map((d) => html`<span role="columnheader">${t(d)}</span>`)}</div>
          ${Array.from({ length: 6 }, (_, week) => html`<div class="pma-calendar__week" role="row">${days.slice(week * 7, week * 7 + 7).map((day) => {
            const entries = byDay.get(day) ?? [];
            const outside = day.slice(0, 7) !== month;
            return html`<div role="gridcell" class="pma-calendar__day${outside ? ' pma-calendar__day--outside' : ''}${day === now ? ' pma-calendar__day--today' : ''}"
                aria-label="${day}${entries.length ? `, ${t('{n} Termine', { n: entries.length })}` : ''}">
              <span class="pma-calendar__date">${Number(day.slice(8))}</span>
              <ul>${entries.map(
                (e) => html`<li><a class="pma-calendar__entry pma-calendar__entry--${e.kind}${e.kind === 'task' && e.status === 'done' ? ' pma-calendar__entry--done' : ''}"
                  href="/projects/${e.project_id}?tab=${KIND_TAB[e.kind]}" title="${t(KIND_LABEL[e.kind]!)}: ${e.title}">${e.title}</a></li>`,
              )}</ul>
            </div>`;
          })}</div>`)}
        </div>
        <p class="pma-muted pma-small pma-calendar__legend"><span class="pma-calendar__entry pma-calendar__entry--task">${t('Aufgabe')}</span>
          <span class="pma-calendar__entry pma-calendar__entry--gate">${t('Gate')}</span>
          <span class="pma-calendar__entry pma-calendar__entry--risk_review">${t('Risikoprüfung')}</span>
          <span class="pma-calendar__entry pma-calendar__entry--phase_end">${t('Phasenende')}</span></p>
      </section>`,
    );
  }

  container.onclick = async (event) => {
    const element = event.target as HTMLElement;
    const shift = element.closest<HTMLElement>('[data-shift]')?.dataset.shift;
    if (shift !== undefined) {
      if (shift === '0') month = today().slice(0, 7);
      else {
        const d = new Date(`${month}-01T00:00:00Z`);
        d.setUTCMonth(d.getUTCMonth() + Number(shift));
        month = iso(d).slice(0, 7);
      }
      setQuery('month', month === today().slice(0, 7) ? null : month);
      await draw().catch(toastError);
    }
    if (element.closest('[data-subscribe]')) {
      try {
        const created = await api.post('/api-tokens', { name: t('Kalender-Abo'), scopes: ['read'] });
        const url = `${location.origin}/api/v1/calendar.ics?token=${created.token}`;
        openFormDialog({
          title: t('Kalender abonnieren'),
          description: t(
            'Diese Adresse in Outlook, Google Kalender oder Apple Kalender als Internetkalender hinzufügen. Sie enthält ein nur-lesendes Token, das du unter Einstellungen jederzeit widerrufen kannst. Der Link wird nur jetzt angezeigt.',
          ),
          fields: html`<label class="p-field pma-span-2"><span class="p-label">${t('Abo-Adresse')}</span><input class="p-input pma-mono" readonly value="${url}"></label>`,
          submitLabel: t('Kopieren'),
          onSubmit: async () => {
            await navigator.clipboard?.writeText(url).catch(() => {});
          },
        });
      } catch (error) {
        toastError(error);
      }
    }
  };

  await draw();
}
